import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { AlertTriangle, BookOpen, FileSearch, Microscope, Tag, Users, Wand2 } from "lucide-react";
import Navigation from "../components/Navigation";
import AdminSubNav from "../components/AdminSubNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAPER_TYPE_LABELS = {
  literature_review: "Literature review",
  journal_article: "Journal article",
  thesis_chapter: "Thesis chapter",
  technical_report: "Technical report",
};

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid={testId}>
    <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wide">
      <Icon className="w-3.5 h-3.5" />{label}
    </div>
    <p className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Fraunces, serif" }}>{value}</p>
  </div>
);

export default function AdminResearchAnalytics({ user, setUser }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("access_token");
        const resp = await axios.get(`${API}/research/admin/analytics`, { headers: { Authorization: `Bearer ${token}` } });
        setData(resp.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Error loading analytics");
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} setUser={setUser} />
      <AdminSubNav user={user} activeTab="research" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-bold text-slate-900 mb-1" style={{ fontFamily: "Fraunces, serif" }} data-testid="research-analytics-title">
          Researcher Analytics
        </h1>
        <p className="text-sm text-slate-500 mb-6">
          Which topics and documents power AI research drafts — use this to guide new acquisitions.
        </p>

        {error && <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm" data-testid="research-analytics-error">{error}</div>}
        {!data && !error && <div className="text-center py-16 text-slate-500">Loading analytics…</div>}

        {data && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <StatCard icon={Microscope} label="Total drafts" value={data.totals.drafts} testId="stat-total-drafts" />
              <StatCard icon={FileSearch} label="Last 30 days" value={data.totals.drafts_30d} testId="stat-drafts-30d" />
              <StatCard icon={Users} label="Researchers" value={data.totals.researchers} testId="stat-researchers" />
              <StatCard icon={Wand2} label="Revisions" value={data.totals.revisions} testId="stat-revisions" />
              <StatCard icon={BookOpen} label="Avg sources/draft" value={data.totals.avg_sources} testId="stat-avg-sources" />
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="research-weekly-chart">
              <h2 className="font-semibold text-slate-900 mb-4" style={{ fontFamily: "Fraunces, serif" }}>Drafts per week (last 12 weeks)</h2>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.weekly} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#ea580c" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="grid lg:grid-cols-[1.5fr_1fr] gap-6">
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="research-top-documents">
                <h2 className="font-semibold text-slate-900 px-5 pt-5 pb-3" style={{ fontFamily: "Fraunces, serif" }}>Most cited documents</h2>
                {data.top_documents.length === 0 ? (
                  <p className="px-5 pb-5 text-sm text-slate-400">No drafts yet.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                      <tr><th className="px-5 py-2">#</th><th className="px-3 py-2">Document</th><th className="px-3 py-2">Year</th><th className="px-3 py-2 text-right pr-5">Drafts citing</th></tr>
                    </thead>
                    <tbody>
                      {data.top_documents.map((doc, index) => (
                        <tr key={doc.item_id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`top-doc-row-${index}`}>
                          <td className="px-5 py-2.5 text-slate-400">{index + 1}</td>
                          <td className="px-3 py-2.5">
                            <Link to={`/items/${doc.item_id}`} className="text-slate-800 hover:text-orange-700 hover:underline line-clamp-1">{doc.title}</Link>
                            <p className="text-xs text-slate-400 line-clamp-1">{doc.authors?.join(", ") || "Unknown author"}</p>
                          </td>
                          <td className="px-3 py-2.5 text-slate-500">{doc.year || "—"}</td>
                          <td className="px-3 py-2.5 pr-5 text-right">
                            <span className="rounded-full bg-orange-50 border border-orange-200 text-orange-700 px-2.5 py-0.5 text-xs font-semibold">{doc.citations}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="space-y-6">
                <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="research-top-topics">
                  <h2 className="font-semibold text-slate-900 mb-3" style={{ fontFamily: "Fraunces, serif" }}>Topics powering drafts</h2>
                  {data.top_topics.length === 0 ? (
                    <p className="text-sm text-slate-400">No data yet.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {data.top_topics.map((topic) => (
                        <span key={topic.tag} className="inline-flex items-center gap-1.5 rounded-full border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs text-orange-800 capitalize">
                          <Tag className="w-3 h-3" />{topic.tag}
                          <span className="font-bold">{topic.citations}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="research-distributions">
                  <h2 className="font-semibold text-slate-900 mb-3" style={{ fontFamily: "Fraunces, serif" }}>Draft profile</h2>
                  <div className="space-y-1.5 text-sm">
                    {data.paper_types.map((pt) => (
                      <div key={pt.paper_type} className="flex justify-between">
                        <span className="text-slate-600">{PAPER_TYPE_LABELS[pt.paper_type] || pt.paper_type}</span>
                        <span className="text-slate-900 font-semibold">{pt.count}</span>
                      </div>
                    ))}
                    <div className="border-t border-slate-100 pt-1.5 mt-1.5">
                      {data.languages.map((lang) => (
                        <div key={lang.language} className="flex justify-between">
                          <span className="text-slate-600">{lang.language}</span>
                          <span className="text-slate-900 font-semibold">{lang.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border border-amber-200 rounded-lg p-5" data-testid="research-unmet-demand">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <h2 className="font-semibold text-slate-900" style={{ fontFamily: "Fraunces, serif" }}>Unmet research demand</h2>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Prompts where the library had too little evidence to draft — the strongest signal for what to acquire next.
              </p>
              {data.unmet_prompts.length === 0 ? (
                <p className="text-sm text-slate-400">No failed retrievals recorded yet.</p>
              ) : (
                <div className="space-y-2">
                  {data.unmet_prompts.map((gap, index) => (
                    <div key={index} className="rounded-md border border-amber-100 bg-amber-50/50 px-3 py-2 flex items-start justify-between gap-3" data-testid={`unmet-prompt-${index}`}>
                      <p className="text-sm text-slate-700 line-clamp-2">{gap.prompt}</p>
                      <div className="shrink-0 text-right">
                        <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-xs font-semibold">×{gap.count}</span>
                        <p className="text-[10px] text-slate-400 mt-0.5">{gap.last_at ? new Date(gap.last_at).toLocaleDateString() : ""}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
